import { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate, useLocation } from 'react-router-dom'
import { Navbar, HStack, Button, CommandBar, SearchResult, Text } from 'wss3-forge'
import { Cart20Regular, ChevronDown20Regular, Book20Regular, Navigation20Regular } from '@fluentui/react-icons'
import { searchDocs, DocSearchResult, getDocPlugins, DocPlugin } from '../lib/docs'

interface DocsHeaderProps {
  onMenuClick?: () => void
  showMenuButton?: boolean
  transparentOnTop?: boolean
}

export function DocsHeader({ onMenuClick, showMenuButton, transparentOnTop }: DocsHeaderProps) {
  const navigate = useNavigate()
  const location = useLocation()

  const [searchOpen, setSearchOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<SearchResult[]>([])
  const [isAtTop, setIsAtTop] = useState(true)
  const [plugins, setPlugins] = useState<DocPlugin[]>([])
  const [docsDropdownOpen, setDocsDropdownOpen] = useState(false)
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 })
  const [docsItemRect, setDocsItemRect] = useState<DOMRect | null>(null)

  const docsOverlayRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    getDocPlugins().then(setPlugins).catch(() => setPlugins([]))
  }, [])

  // Find Documentation nav item position
  useEffect(() => {
    const findDocsItem = () => {
      const navItems = document.querySelectorAll('nav span')
      for (const item of navItems) {
        if (item.textContent === 'Documentation') {
          setDocsItemRect(item.getBoundingClientRect())
          break
        }
      }
    }
    const timeout = setTimeout(findDocsItem, 100)
    window.addEventListener('resize', findDocsItem)
    return () => {
      clearTimeout(timeout)
      window.removeEventListener('resize', findDocsItem)
    }
  }, [])

  useEffect(() => {
    const handleScroll = () => setIsAtTop(window.scrollY < 10)
    window.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll()
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setSearchOpen(true)
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [])

  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([])
      return
    }
    const timeout = setTimeout(async () => {
      try {
        const results = await searchDocs(searchQuery)
        setSearchResults(results.map((r: DocSearchResult) => ({
          id: r.page_id,
          type: r.result_type,
          title: r.page_title,
          subtitle: r.result_type === 'plugin'
            ? 'Plugin Documentation'
            : `${r.plugin_name} / ${r.section_title}`,
          route: r.result_type === 'plugin'
            ? `/${r.plugin_slug}`
            : `/${r.plugin_slug}/${r.section_slug}/${r.page_slug}`
        })))
      } catch {
        setSearchResults([])
      }
    }, 200)
    return () => clearTimeout(timeout)
  }, [searchQuery])

  // Update dropdown position
  useEffect(() => {
    if (docsDropdownOpen && docsItemRect) {
      setDropdownPosition({
        top: docsItemRect.bottom + 12,
        left: docsItemRect.left + docsItemRect.width / 2
      })
    }
  }, [docsDropdownOpen, docsItemRect])

  const handleSearch = (query: string): SearchResult[] => {
    setSearchQuery(query)
    return searchResults
  }

  const getActiveId = (): string => {
    const path = location.pathname
    if (path === '/support') return 'support'
    // Documentation is active for home and any plugin page
    return 'docs'
  }

  const handleNavigate = (id: string) => {
    if (id === 'docs') navigate('/')
    else if (id === 'support') navigate('/support')
  }

  const isTransparent = transparentOnTop && isAtTop

  const Logo = (
    <HStack gap="sm" align="center">
      {showMenuButton && (
        <div
          className="clickable"
          onClick={onMenuClick}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 8 }}
        >
          <Navigation20Regular style={{ color: '#FFFFFF', width: 20, height: 20 }} />
        </div>
      )}
      <a
        href="/"
        onClick={(e) => { e.preventDefault(); navigate('/') }}
        style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}
      >
        <img src="/logo_docs.png" alt="Murphy's Docs" style={{ height: 32 }} />
      </a>
    </HStack>
  )

  // Portal dropdown
  const dropdownPortal = docsDropdownOpen && createPortal(
    <div
      onMouseEnter={() => setDocsDropdownOpen(true)}
      onMouseLeave={() => setDocsDropdownOpen(false)}
      style={{
        position: 'fixed',
        top: dropdownPosition.top,
        left: dropdownPosition.left,
        transform: 'translateX(-50%)',
        zIndex: 9999
      }}
    >
      <div
        style={{
          backgroundColor: 'rgba(22, 22, 22, 0.95)',
          backdropFilter: 'blur(12px)',
          borderRadius: '12px',
          padding: '8px',
          minWidth: '220px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)'
        }}
      >
        {plugins.length === 0 ? (
          <Text size="sm" style={{ color: 'var(--text-muted)', padding: '10px 14px' }}>
            No documentation available
          </Text>
        ) : (
          plugins.map(plugin => (
            <div
              key={plugin.slug}
              className="clickable nav-dropdown-item"
              onClick={() => {
                navigate(`/${plugin.slug}`)
                setDocsDropdownOpen(false)
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '10px 14px',
                borderRadius: '8px'
              }}
            >
              {plugin.logo_url ? (
                <img src={plugin.logo_url} alt="" style={{ width: 20, height: 20, borderRadius: 4, objectFit: 'cover' }} />
              ) : (
                <Book20Regular style={{ width: 20, height: 20, color: 'var(--text-muted)' }} />
              )}
              <span style={{ color: '#B0B0B0', fontSize: '14px', fontWeight: 500 }}>
                {plugin.name}
              </span>
            </div>
          ))
        )}
      </div>
    </div>,
    document.body
  )

  // Chevron overlay on Documentation item
  const docsOverlay = docsItemRect && (
    <div
      ref={docsOverlayRef}
      onMouseEnter={() => setDocsDropdownOpen(true)}
      onMouseLeave={() => setDocsDropdownOpen(false)}
      style={{
        position: 'fixed',
        top: docsItemRect.top,
        left: docsItemRect.left,
        width: docsItemRect.width + 24,
        height: docsItemRect.height,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        zIndex: 1001,
        cursor: 'pointer'
      }}
    >
      <ChevronDown20Regular
        style={{
          color: docsDropdownOpen ? '#FFFFFF' : '#9CA3AF',
          width: 16,
          height: 16,
          transform: docsDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: 'transform 0.2s ease'
        }}
      />
    </div>
  )

  return (
    <>
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          backgroundColor: isTransparent ? 'transparent' : 'var(--bg-primary)',
          transition: 'background-color 0.2s ease'
        }}
      >
        <Navbar
          logo={Logo}
          items={[
            { id: 'docs', label: 'Documentation' },
            { id: 'support', label: 'Support' }
          ]}
          activeId={getActiveId()}
          onNavigate={handleNavigate}
          actions={
            <HStack gap="md" align="center">
              <Button
                variant="secondary"
                icon={<Cart20Regular />}
                onClick={() => window.open('https://murphy-workshop.com', '_blank')}
              >
                Shop
              </Button>
            </HStack>
          }
          sticky={false}
          variant="minimal"
          transparent={isTransparent}
          showSearch
          onSearchClick={() => setSearchOpen(true)}
        />
      </div>

      {docsOverlay}
      {dropdownPortal}

      <CommandBar
        open={searchOpen}
        onClose={() => { setSearchOpen(false); setSearchQuery(''); setSearchResults([]) }}
        placeholder="Search documentation..."
        onSearch={handleSearch}
        onResultSelect={(result) => {
          if (result.route) {
            navigate(result.route)
            setSearchOpen(false)
            setSearchQuery('')
            setSearchResults([])
          }
        }}
        isNaturalLanguage={() => false}
      />
    </>
  )
}
